import { Icon } from "@iconify/react";
import { useTheme } from "../../hooks/useTheme";
import { Colors } from "../../utils/colors";
import {
  Event,
  PromoCode,
  PromoCodeStatus,
  TicketV2,
  NotificationType,
} from "@markit/common.types";
import React, { useCallback, useMemo, useState } from "react";
import { API } from "../../API";
import { PromoCodeModal } from "./DashboardModals/PromoCodeModal";
import { PROMO_FLEX_SECTIONS, PromoCodeItem } from "./PromoCodes/PromoCodeItem";
import { onSnapshot, query, where } from "../../firebase";
import { getTicketsRef } from "../../utils/FirebaseUtils";
import { eventHasNotFreeTickets, uniqueVals } from "@markit/common.utils";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import SearchBoxContainer from "../Containers/SearchBoxContainer";
import filter from "lodash.filter";
import BlackHoverButton from "../Buttons/BlackHoverButton";
import GridTableHeader from "../Headers/GridTableHeader";
import EmptyStateButton from "../Buttons/EmptyStateButton";
import AlertContainer from "../Containers/AlertContainer";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import { DropdownMenuItem } from "../DropdownMenu";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { useDispatch } from "react-redux";
import ListTabs from "../DropdownsAndTabs/ListTabs";
import { reactivatePromoCode } from "../../redux/slices/eventSlice";

type PromoCodePanelProps = {
  event: Event;
};

export const PromoCodePanel = (props: PromoCodePanelProps) => {
  const { event } = props;
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const [freeTicketCodes, setFreeTicketCodes] = useState<TicketV2[]>([]);
  const [isPromoModalVisible, setIsPromoModalVisible] =
    useState<boolean>(false);
  const [promoItem, setPromoItem] = useState<PromoCode | TicketV2 | undefined>(
    undefined
  );
  const [freePromoRedeemed, setFreePromoRedeemed] = useState<boolean>(false);
  const [freeTicketsTotalCount, setFreeTicketsTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState("");

  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });
  const [deletePromoConfirm, setDeletePromoConfirm] = useState(false);
  const [deactivatePromoConfirm, setDeactivatePromoConfirm] = useState(false);
  const [redeemedPromoConfirm, setRedeemedPromoConfirm] = useState(false);
  const [promoTabValue, setPromoTabValue] = useState(0);

  const styles = {
    containerView: {
      paddingInline: 14,
    },

    overallContainerView: {
      width: "100%",
      backgroundColor: theme.SecondaryBG.backgroundColor,
      borderRadius: 20,
    },
  };

  // Get all promo codes
  const allCodes = useMemo(() => {
    const namedPromoCodes = event
      ? event.promoCodes.filter((code) => code.alias)
      : [];
    const codes: (TicketV2 | PromoCode)[] = [...namedPromoCodes];
    const uniqueFreeTicketAliases = uniqueVals(
      freeTicketCodes,
      (code) => code.alias
    );
    return codes.concat(uniqueFreeTicketAliases);
  }, [event, freeTicketCodes]);

  const tabLabels = useMemo(() => ["Active", "Inactive"], []);

  // Free ticket codes don't carry a PromoCodeStatus (only discount promo codes
  // can be deactivated), so they always show up under the Active tab.
  const activeCodes = useMemo(
    () =>
      allCodes.filter((code) => {
        const promoCode = code as PromoCode;
        return (
          promoCode.type === undefined ||
          promoCode.status === PromoCodeStatus.ACTIVE
        );
      }),
    [allCodes]
  );

  const inactiveCodes = useMemo(
    () =>
      allCodes.filter((code) => {
        const promoCode = code as PromoCode;
        return (
          promoCode.type !== undefined &&
          promoCode.status === PromoCodeStatus.INACTIVE
        );
      }),
    [allCodes]
  );

  const codesForTab = useMemo(
    () => (promoTabValue === 0 ? activeCodes : inactiveCodes),
    [promoTabValue, activeCodes, inactiveCodes]
  );

  const handleSearch = (text: string) => {
    setSearchTerm(text.toLowerCase());
  };

  const contains = useCallback((item: PromoCode | TicketV2, query: string) => {
    return item.alias.toLowerCase().includes(query);
  }, []);

  const promoCodesToShow = useMemo(() => {
    if (searchTerm === "") {
      return codesForTab;
    } else {
      return filter(codesForTab, (code: PromoCode | TicketV2) => {
        return contains(code, searchTerm);
      });
    }
  }, [contains, codesForTab, searchTerm]);

  useAsyncOnMount(async () => {
    const ticketsRef = getTicketsRef(event.id);
    const querySnapshot = query(
      ticketsRef,
      where("originalOwner", "==", event.createdBy),
      where("alias", "!=", "")
    );
    const unsubscribe = onSnapshot(querySnapshot, (snapshot) => {
      setFreeTicketsTotalCount(snapshot.size);

      setFreeTicketCodes((prev) => {
        const updated = [...prev];
        snapshot.docChanges().forEach((change) => {
          const newTicketCode = change.doc.data();
          if (change.type === "added" || change.type === "modified") {
            const byIdIndex = updated.findIndex(
              (fetched) => fetched.id === newTicketCode.id
            );
            if (byIdIndex !== -1) {
              updated[byIdIndex] = newTicketCode;
            } else {
              const aliasIndex = updated.findIndex(
                (fetched) => fetched.alias === newTicketCode.alias
              );
              if (aliasIndex === -1) {
                updated.push(newTicketCode);
              } else if (change.type === "modified") {
                updated[aliasIndex] = newTicketCode;
              }
            }
          } else if (change.type === "removed") {
            const index = updated.findIndex(
              (fetched) => fetched.alias === newTicketCode.alias
            );
            if (index !== -1) {
              updated.splice(index, 1);
            }
          }
        });
        return updated;
      });
    });
    return () => unsubscribe();
  });

  const promoLink = useMemo(() => {
    if (promoItem) {
      // todo (jonathan): not the cleanest way to do this
      const promoCodeItem = promoItem as PromoCode;

      const link =
        promoCodeItem.type !== undefined
          ? `https://markitai.com/e/${event.id}?promoCodeId=${promoItem.id}`
          : `Use code ${promoItem.alias} during checkout for free ticket. https://markitai.com/e/${event.id}`;

      return link;
    } else {
      return "";
    }
  }, [event.id, promoItem]);

  const copyPromoLink = useCallback(
    async (fullLink?: boolean) => {
      if (promoItem) {
        // todo (jonathan): not the cleanest way to do this

        navigator.clipboard
          .writeText(fullLink ? promoLink : promoItem.alias)
          .then(() => {
            showNotificationBanner(
              dispatch,
              fullLink ? "Copied Link!" : "Copied Name!",
              NotificationType.AFFIRMATIVE
            );
          })
          .catch((err) => {
            setAlertText({ heading: err, subHeading: "" });
          });
        setPromoItem(undefined);
      }
    },
    [dispatch, promoItem, promoLink]
  );

  const editPromo = useCallback(() => {
    setIsPromoModalVisible(true);
  }, []);

  const deleteFreePromoCode = useCallback(async () => {
    if (promoItem) {
      await API.promoCodes
        .deleteFreeTicketCode({
          eventId: event.id,
          alias: promoItem.alias,
        })
        .then((response) => {
          if (!response.success) {
            setAlertText({
              heading: "Error deleting free promo.",
              subHeading: "Please try again.",
            });
          }
        })
        .catch(() => {
          setAlertText({
            heading: "Error deleting free promo.",
            subHeading: "Please try again.",
          });
        });
    }
  }, [event.id, promoItem]);

  const deletePromoCode = useCallback(async () => {
    if (promoItem) {
      // todo (jonathan): not the cleanest way to do this
      const promoCodeItem = promoItem as PromoCode;
      if (promoCodeItem.type !== undefined) {
        // The backend keeps a single delete-promo-code endpoint that branches
        // internally: it deactivates the code instead of deleting it once it's
        // been used, so the used-codes/refund-logic history stays intact.
        const isDeactivating = promoCodeItem.usedCount > 0;
        await API.promoCodes
          .deletePromoCode({
            eventId: event.id,
            promoCodeId: promoItem.id,
          })
          .then((response) => {
            if (!response.success) {
              setAlertText({
                heading: `Error ${
                  isDeactivating ? "deactivating" : "deleting"
                } promo code.`,
                subHeading: "Please try again.",
              });
            } else {
              showNotificationBanner(
                dispatch,
                isDeactivating ? "Promo Code Inactive" : "Promo Code Deleted",
                NotificationType.NEGATIVE,
                isDeactivating ? undefined : "ion:trash"
              );
            }
          })
          .catch(() => {
            setAlertText({
              heading: `Error ${
                isDeactivating ? "deactivating" : "deleting"
              } promo code.`,
              subHeading: "Please try again.",
            });
          });
      } else {
        // Redeemed free ticket codes are routed straight to the
        // redeemedPromoConfirm modal from the dropdown, so this only ever
        // runs for codes that haven't been redeemed yet.
        await deleteFreePromoCode();
      }
      setPromoItem(undefined);
    }
  }, [deleteFreePromoCode, dispatch, event.id, promoItem]);

  const confirmDeleteFreePromoCode = useCallback(async () => {
    if (promoItem) {
      await deleteFreePromoCode();
      setPromoItem(undefined);
    }
  }, [deleteFreePromoCode, promoItem]);

  const reactivatePromo = useCallback(
    (item: PromoCode) => {
      dispatch(reactivatePromoCode(event, item.id));
      setPromoItem(undefined);
      showNotificationBanner(
        dispatch,
        "Promo Code Re-Activated",
        NotificationType.AFFIRMATIVE
      );
    },
    [dispatch, event]
  );

  // Computed per-row rather than a single static list, since the delete/deactivate
  // label and action depend on that specific code's usedCount.
  const getDropdownItems = useCallback(
    (item: PromoCode | TicketV2): DropdownMenuItem[] => {
      const promoCodeItem = item as PromoCode;
      const isDiscountCode = promoCodeItem.type !== undefined;
      const alreadyUsed = isDiscountCode && promoCodeItem.usedCount > 0;
      const isInactive =
        isDiscountCode && promoCodeItem.status === PromoCodeStatus.INACTIVE;

      return [
        {
          title: "Copy Name",
          icon: "feather:link-2",
          key: "copy_link",
          onPress: () => copyPromoLink(),
        },
        {
          title: "Share Link",
          icon: "mdi:share",
          key: "share_link",
          onPress: () => copyPromoLink(true),
        },
        {
          title: "Edit Promo",
          icon: "ion:create",
          key: "edit_promo",
          onPress: editPromo,
        },
        isInactive
          ? {
              title: "Activate Promo",
              icon: "ion:checkmark-circle",
              key: "activate_promo",
              onPress: () => reactivatePromo(promoCodeItem),
            }
          : {
              title: "Delete Promo",
              icon: "ion:trash",
              key: "delete_promo",
              onPress: () => {
                if (!isDiscountCode && freePromoRedeemed) {
                  setRedeemedPromoConfirm(true);
                } else if (alreadyUsed) {
                  setDeactivatePromoConfirm(true);
                } else {
                  setDeletePromoConfirm(true);
                }
              },
              isDestructive: true,
            },
      ];
    },
    [copyPromoLink, editPromo, reactivatePromo, freePromoRedeemed]
  );

  return (
    <div>
      <StandardBorderedContainer
        containerStyles={{
          ...styles.overallContainerView,
          paddingBottom: 14,
        }}
      >
        <div className="ColumnNormal" style={{ minHeight: 770 }}>
          <div
            className="ColumnNormal"
            style={{ ...styles.containerView, paddingBlock: 14, gap: 14 }}
          >
            <div className="AlignedRowSpaced" style={{ gap: 12 }}>
              <SearchBoxContainer
                placeholder="Search Promo Codes..."
                onChange={(e) => handleSearch(e.target.value)}
                containerStyles={{ marginTop: 0 }}
              />
              <BlackHoverButton
                title={"New Promo"}
                iconName="ion:add-circle"
                iconSize={19}
                onPress={() => {
                  setPromoItem(undefined);
                  setIsPromoModalVisible(true);
                }}
                permanentHover
                containerStyles={{
                  paddingInline: 14,
                  paddingBlock: 12,
                  whiteSpace: "nowrap",
                }}
                disabled={!eventHasNotFreeTickets(event)}
              />
            </div>
            <ListTabs
              tabLabels={tabLabels}
              tabNumbers={[-1, -1]}
              selectedValue={promoTabValue}
              onChange={setPromoTabValue}
              altColor={Colors.BLACK}
            />
          </div>
          <HorizontalDivider />
          <GridTableHeader
            flexSections={PROMO_FLEX_SECTIONS}
            sectionTitles={["Promo Code", "Discount", "Uses"]}
          />
          <HorizontalDivider />
          <div style={{ marginTop: 14 }}>
            {codesForTab.length > 0 ? (
              promoCodesToShow.length === 0 ? (
                <EmptyStateButton
                  title="No Results"
                  description="Check for typos or search for something else"
                  icon={
                    <Icon
                      icon="ion:search"
                      height={40}
                      style={{ color: Colors.GRAY1 }}
                    />
                  }
                  btnText={undefined}
                  onPress={() => {}}
                  containerStyles={{ paddingTop: 120 }}
                />
              ) : (
                promoCodesToShow.map((item, index) => {
                  return (
                    <PromoCodeItem
                      key={index}
                      item={item}
                      event={event}
                      index={index}
                      totalItems={codesForTab.length}
                      setItem={setPromoItem}
                      setFreePromoRedeemed={setFreePromoRedeemed}
                      freeTicketsTotalCount={freeTicketsTotalCount}
                      getDropdownItems={getDropdownItems}
                    />
                  );
                })
              )
            ) : promoTabValue === 0 ? (
              <EmptyStateButton
                title={"No Promo Codes"}
                btnText={eventHasNotFreeTickets(event) ? "New Promo" : ""}
                description={
                  eventHasNotFreeTickets(event)
                    ? "Create a new promo code"
                    : "You can't create promo codes for a free event"
                }
                icon={
                  <Icon
                    icon={"ion:pricetags"}
                    height={49}
                    style={{ color: Colors.GRAY1 }}
                  />
                }
                iconBox={73}
                onPress={() => {
                  setPromoItem(undefined);
                  setIsPromoModalVisible(true);
                }}
                containerStyles={{ paddingTop: 120 }}
              />
            ) : (
              <EmptyStateButton
                title={"No Inactive Promo Codes"}
                description="Used promo codes that have been deactivated will show up here"
                icon={
                  <Icon
                    icon={"ion:pricetags"}
                    height={49}
                    style={{ color: Colors.GRAY1 }}
                  />
                }
                iconBox={73}
                btnText={undefined}
                onPress={() => {}}
                containerStyles={{ paddingTop: 120 }}
              />
            )}
          </div>
        </div>
      </StandardBorderedContainer>
      {isPromoModalVisible ? (
        <PromoCodeModal
          event={event}
          setIsPromoModalVisible={setIsPromoModalVisible}
          item={promoItem}
          setItem={setPromoItem}
        />
      ) : null}

      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        theme={theme}
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
      {/* For discount promo codes that haven't been used */}
      <ConfirmDeleteModal
        heading="Are you sure you want to delete this promo?"
        subtext="Purchasers will no longer be able to use this."
        deleteButtonText="Delete"
        hideModal={!deletePromoConfirm}
        setIsVisible={setDeletePromoConfirm}
        deleteOnPress={deletePromoCode}
        theme={theme}
      />
      {/* For discount promo codes that have been used */}
      <ConfirmDeleteModal
        heading="Are you sure you want to delete this promo?"
        subtext="Since it's already been used, it will be changed to inactive instead of deleted. Inactive means it can't be redeemed at checkout, but can be changed back to active at any time. Purchasers who already used it won't be affected."
        deleteButtonText="Delete"
        hideModal={!deactivatePromoConfirm}
        setIsVisible={setDeactivatePromoConfirm}
        deleteOnPress={deletePromoCode}
        theme={theme}
      />
      {/* For free promo codes that have at least one use made */}
      <ConfirmDeleteModal
        heading="This free promo code has already been redeemed at least once"
        subtext="Deleting it will remove any remaining unclaimed tickets, but tickets that have already been redeemed will stay valid."
        deleteButtonText="Delete"
        hideModal={!redeemedPromoConfirm}
        setIsVisible={setRedeemedPromoConfirm}
        deleteOnPress={confirmDeleteFreePromoCode}
        theme={theme}
      />
    </div>
  );
};
